import { useState, type ReactElement } from 'react';
import styled from '@emotion/styled';
import FilterListIcon from '@mui/icons-material/FilterList';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import SearchIcon from '@mui/icons-material/Search';
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {} from '../../contexts/default.js';

// ============================================================================
// Types
// ============================================================================

type HcpRow = {
  id: string;
  name: string;
  isKol: boolean;
  primaryAffiliation: string;
  location: string;
  email: string;
  phone: string | null;
};

// ============================================================================
// Mock data
// ============================================================================

const MOCK_HCPS: HcpRow[] = [
  {
    id: '1',
    name: 'HCP Numero Uno',
    isKol: false,
    primaryAffiliation: 'Lincoln Behavorial Svc',
    location: '1111 W Outer Dr, Detroit, MI, 48235',
    email: 'numero1@lbsheath.ios',
    phone: '+1 (248) 3...',
  },
  {
    id: '2',
    name: 'HCP Numero Dos',
    isKol: true,
    primaryAffiliation: 'Iu Hlth Univ Hosp',
    location: '23423 University Blvd, Indianapolis, IN, 46202',
    email: 'numero2@iupui.edu',
    phone: '+1 (317) 2...',
  },
  {
    id: '3',
    name: 'HCP Numero Tres',
    isKol: false,
    primaryAffiliation: 'Taylor Health Ctr',
    location: '23432 Taylor Dr, Flint, MI, 48507',
    email: 'numero3@taylorlifecenter.org',
    phone: null,
  },
  {
    id: '4',
    name: 'HCP Numero Cuatro',
    isKol: false,
    primaryAffiliation: 'Stanton Hospital',
    location: '901 Memorial Rd, Birmingham, MI, 49931',
    email: 'numero4@nbhs.org',
    phone: '+1 (804) 8...',
  },
  {
    id: '5',
    name: 'HCP Numero Cinco',
    isKol: true,
    primaryAffiliation: 'HealthWest Health',
    location: '4343 E Apple Ave, Muskegon, MI, 49442',
    email: 'numero5@umich.edu',
    phone: '+1 (313) 3...',
  },
  {
    id: '6',
    name: 'HCP Numero Seis',
    isKol: false,
    primaryAffiliation: 'Oaklawn Health',
    location: '2324 Vista Dr, ...',
    email: 'numero6@pinesbhs.or...',
    phone: '...',
  },
];

const TOTAL_RESULTS = 75;

// ============================================================================
// Component
// ============================================================================

export const MslTerritoryInsights = (): ReactElement => {
  // const user = useCustomPageUser();
  // const brand = useCustomPageBrand();
  // const subject = useCustomPageSubject();
  // const arbitraryFilterFields = useCustomPageArbitraryFilterFields();
  // const setArbitraryFilterFields = useSetCustomPageArbitraryFilterFields();
  // const dispatchAction = useCustomPageDispatchAction();

  const [nameSearch, setNameSearch] = useState<string | null>(null);
  const [addressSearch, setAddressSearch] = useState<string | null>(null);

  const hcps = MOCK_HCPS;

  return (
    <Root>
      <Paper elevation={0} sx={{ p: 2, maxWidth: '80vw', borderRadius: '5px' }}>
        <Typography variant="body2" sx={{ mb: 2 }}>
          These are the potential HCPs that you can engage with in your territory.
        </Typography>

        {/* HCP Table */}
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <FilterableHeader label="Name" />
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Profile</TableCell>
                <FilterableHeader label="Primary Affiliation" />
                <FilterableHeader label="Location" />
                <FilterableHeader label="Email" />
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Phone</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {hcps.map((hcp) => (
                <TableRow key={hcp.id} hover>
                  <TableCell sx={{ fontSize: '0.8rem' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {hcp.name}
                      {hcp.isKol && (
                        <KolBadge>KOL</KolBadge>
                      )}
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: '0.7rem',
                        borderColor: '#eb1700',
                        color: '#eb1700',
                        textTransform: 'none',
                        borderRadius: '16px',
                        px: 1.5,
                        py: 0.25,
                        '&:hover': { borderColor: '#c41200', color: '#c41200' },
                      }}
                    >
                      → View
                    </Button>
                  </TableCell>

                  <TableCell sx={{ fontSize: '0.8rem' }}>{hcp.primaryAffiliation}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{hcp.location}</TableCell>

                  <TableCell sx={{ fontSize: '0.8rem' }}>
                    {hcp.email ? (
                      <Link href={`mailto:${hcp.email}`} underline="hover" color="inherit">
                        {hcp.email}
                      </Link>
                    ) : null}
                  </TableCell>

                  <TableCell sx={{ fontSize: '0.8rem', color: hcp.phone ? 'inherit' : '#aaa' }}>
                    {hcp.phone ?? 'None'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Showing {hcps.length} of {TOTAL_RESULTS} results
            </Typography>
          </Box>
        </TableContainer>

        {/* HCP Search Section */}
        <Typography variant="body2" sx={{ mb: 2 }}>
          Or if you're looking for a different HCP, get started with an HCP search.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxWidth: 420 }}>
          <Typography variant="body2" fontWeight={600}>
            Search by Name
          </Typography>

          <Autocomplete
            freeSolo
            options={[]}
            value={nameSearch}
            onChange={(_e, v) => setNameSearch(v)}
            renderInput={(params) => (
              <TextField {...params} placeholder="Name" size="small" variant="outlined" />
            )}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 0.5 }}>
            <Divider sx={{ flex: 1 }} />
            <Typography variant="body2" color="text.secondary">
              OR
            </Typography>
            <Divider sx={{ flex: 1 }} />
          </Box>

          <Typography variant="body2" fontWeight={600}>
            Search by address or current location
          </Typography>

          <Autocomplete
            freeSolo
            options={[]}
            value={addressSearch}
            onChange={(_e, v) => setAddressSearch(v)}
            renderInput={(params) => (
              <TextField {...params} placeholder="Start typing an address" size="small" variant="outlined" />
            )}
          />

          <Link
            component="button"
            variant="body2"
            underline="hover"
            sx={{ color: '#eb1700', display: 'flex', alignItems: 'center', gap: 0.5, width: 'fit-content' }}
          >
            <MyLocationIcon sx={{ fontSize: 14 }} />
            Share my current location
          </Link>

          <Button
            variant="outlined"
            size="small"
            startIcon={<SearchIcon />}
            sx={{
              textTransform: 'none',
              color: 'text.secondary',
              borderColor: '#ccc',
              width: 'fit-content',
              fontSize: '0.8rem',
            }}
          >
            Search for profiles.
          </Button>
        </Box>
      </Paper>
    </Root>
  );
};

// ============================================================================
// Sub-components
// ============================================================================

const FilterableHeader = ({ label }: { label: string }): ReactElement => (
  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      {label}
      <IconButton aria-label={`Filter ${label}`} size="small" sx={{ p: 0.25 }}>
        <FilterListIcon sx={{ fontSize: 14, color: '#aaa' }} />
      </IconButton>
    </Box>
  </TableCell>
);

// ============================================================================
// Styled
// ============================================================================

const Root = styled.div({
  padding: '24px',
  boxSizing: 'border-box',
  width: '100%',
});

const KolBadge = styled.span({
  fontSize: '0.65rem',
  fontWeight: 600,
  color: '#eb1700',
  lineHeight: 1,
});